/**
 * `<app-tour>`: the page goes dim, one real control stays lit, a card says
 * what to do.
 *
 * The dim is four boxes around the lit area, not one sheet with a hole, so
 * clicks reach the real control and nothing else. A frame loop follows the
 * control while the page scrolls, a menu opens, or the panel resizes. The
 * card is drawn by Lit only when the step or its state changes.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { finishTour, lockedBy, moveStep, runningTour, startTour, stopTour } from './tour-engine.ts';
import { resolveTarget, tourById, type TourStep } from './tour-registry.ts';

/** A missing control gets this long to appear before the card says so. */
const MISSING_AFTER_MS = 2500;
const PAD = 6;
const GAP = 14;
const CARD_W = 300;

type Mode = 'ok' | 'waiting' | 'missing' | 'failed';
/** Give a step this long before its `failed` check counts (a plot takes a frame to start). */
const FAILED_AFTER_MS = 1200;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The control, plus any drop-down menu or panel it has open (they hang outside its box). */
function litBox(el: HTMLElement): Box | null {
  const rects = [el, ...el.querySelectorAll<HTMLElement>('.combobox.open .combobox-menu, .dash-dd-panel')]
    .map((e) => e.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (!rects.length) return null;
  const x = Math.min(...rects.map((r) => r.left)) - PAD;
  const y = Math.min(...rects.map((r) => r.top)) - PAD;
  const right = Math.max(...rects.map((r) => r.right)) + PAD;
  const bottom = Math.max(...rects.map((r) => r.bottom)) + PAD;
  return { x, y, w: right - x, h: bottom - y };
}

@customElement('app-tour')
export class AppTour extends LightElement {
  private store = new StoreController(this, [runningTour]);
  @state() private mode: Mode = 'ok';
  @state() private failure: string | null = null;
  /**
   * The step was done when we arrived (after Back, or a ship already picked):
   * it does not move on by itself, which would undo the Back, and shows Next.
   */
  @state() private doneAtEntry = false;
  private frame = 0;
  private stepKey = '';
  private stepSince = 0;
  private sawTarget = false;
  private scrolled = false;
  private target: HTMLElement | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('keydown', this.onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('keydown', this.onKey);
    cancelAnimationFrame(this.frame);
  }

  private step(): TourStep | null {
    const run = runningTour.value;
    return run ? run.steps[run.index] ?? null : null;
  }

  /** Controls this step blocks, marked so CSS shows them faint. */
  private blockedEls: HTMLElement[] = [];

  private markBlocked(step: TourStep | null): void {
    const next = (step?.blocked ?? []).flatMap((id) => [
      ...document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(id)}"]`),
    ]);
    for (const el of this.blockedEls) if (!next.includes(el)) el.removeAttribute('data-tour-blocked');
    for (const el of next) el.setAttribute('data-tour-blocked', '');
    this.blockedEls = next;
  }

  /** A click on the lit control moves on, after the control has done its own work. */
  private onClick = (e: MouseEvent): void => {
    // A blocked control does nothing while its step is showing.
    if (e.target instanceof Element && e.target.closest('[data-tour-blocked]')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const step = this.step();
    if (!step || step.advance !== 'click' || !this.target) return;
    if (!(e.target instanceof Node) || !this.target.contains(e.target)) return;
    const key = this.stepKey;
    setTimeout(() => {
      if (this.stepKey === key) moveStep(1);
    }, 60);
  };

  /** Escape stops the tour, unless it is closing a menu inside the lit control. */
  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !runningTour.value) return;
    if (this.target?.querySelector('.combobox.open')) return;
    // Escape in a text box inside the lit control cancels the typing, not the tour.
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && this.target?.contains(active)) return;
    stopTour();
  };

  override updated(): void {
    if (!runningTour.value) this.markBlocked(null);
    cancelAnimationFrame(this.frame);
    if (runningTour.value) this.frame = requestAnimationFrame(this.tick);
  }

  private tick = (): void => {
    const run = runningTour.value;
    const step = this.step();
    if (!run || !step) return;
    const key = `${run.tour.id}:${run.index}`;
    const now = performance.now();
    if (key !== this.stepKey) {
      this.stepKey = key;
      this.stepSince = now;
      this.sawTarget = false;
      this.scrolled = false;
      // Passed over in the direction of travel, so Back does not land on it again.
      if (step.skipIf?.()) {
        this.stepKey = '';
        moveStep(run.cameBack && run.index > 0 ? -1 : 1);
        return;
      }
      step.enter?.();
      this.doneAtEntry = !!run.cameBack || (typeof step.advance === 'object' && step.advance.when());
    }

    // Before the target check: a saved chart closes its dialog in the same moment.
    if (!this.doneAtEntry && typeof step.advance === 'object' && step.advance.when()) {
      // A step that is done closes the menu it left open (the ship list stays
      // open after a pick, for picking more): one click outside, as a person would.
      if (this.target?.querySelector('.combobox.open')) document.body.click();
      moveStep(1);
      return;
    }
    // Every frame: a menu item exists only while its menu is open.
    this.markBlocked(step);
    const ready = step.ready ? step.ready() : true;
    const el = ready ? resolveTarget(step) : null;
    const box = el ? litBox(el) : null;
    this.target = box ? el : null;

    if (box) {
      this.sawTarget = true;
      if (!this.scrolled) {
        this.scrolled = true;
        el!.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } else if (step.backIfGone && this.sawTarget && !this.doneAtEntry) {
      moveStep(-1);
      return;
    }

    const failure = !box && step.failed && now - this.stepSince > FAILED_AFTER_MS ? step.failed() : null;
    if (failure !== this.failure) this.failure = failure;
    const mode: Mode = box || (this.doneAtEntry && ready)
      ? 'ok'
      : failure
        ? 'failed'
        : !ready
          ? 'waiting'
          : now - this.stepSince > MISSING_AFTER_MS
            ? 'missing'
            : 'waiting';
    if (mode !== this.mode) this.mode = mode;
    this.place(box);
    this.frame = requestAnimationFrame(this.tick);
  };

  private place(box: Box | null): void {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const b = box ?? { x: W / 2, y: H / 2, w: 0, h: 0 };
    const set = (sel: string, x: number, y: number, w: number, h: number): void => {
      const el = this.querySelector<HTMLElement>(sel);
      if (!el) return;
      el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
      el.style.width = `${Math.max(0, Math.round(w))}px`;
      el.style.height = `${Math.max(0, Math.round(h))}px`;
    };
    set('.tour-dim.t', 0, 0, W, b.y);
    set('.tour-dim.b', 0, b.y + b.h, W, H - b.y - b.h);
    set('.tour-dim.l', 0, b.y, b.x, b.h);
    set('.tour-dim.r', b.x + b.w, b.y, W - b.x - b.w, b.h);
    const ring = this.querySelector<HTMLElement>('.tour-ring');
    if (ring) {
      ring.hidden = !box;
      set('.tour-ring', b.x, b.y, b.w, b.h);
    }

    const card = this.querySelector<HTMLElement>('.tour-card');
    if (!card) return;
    const ch = card.offsetHeight;
    let cx: number;
    let cy: number;
    if (!box) {
      cx = (W - CARD_W) / 2;
      cy = (H - ch) / 2;
    } else if (b.x + b.w + GAP + CARD_W < W - 8) {
      cx = b.x + b.w + GAP;
      cy = b.y;
    } else if (b.x - GAP - CARD_W > 8) {
      cx = b.x - GAP - CARD_W;
      cy = b.y;
    } else if (b.y + b.h + GAP + ch < H - 8) {
      cx = Math.min(Math.max(8, b.x), W - CARD_W - 8);
      cy = b.y + b.h + GAP;
    } else if (b.y - GAP - ch > 8) {
      cx = Math.min(Math.max(8, b.x), W - CARD_W - 8);
      cy = b.y - GAP - ch;
    } else {
      // A dialog that fills the screen: the bottom-right corner, over its edge.
      cx = W - CARD_W - 20;
      cy = H - ch - 20;
    }
    cy = Math.min(Math.max(8, cy), H - ch - 8);
    card.style.transform = `translate(${Math.round(cx)}px, ${Math.round(cy)}px)`;
  }

  private renderCard(): TemplateResult {
    const run = runningTour.value!;
    const step = run.steps[run.index]!;
    const n = run.steps.length;
    const last = run.index === n - 1;
    const meta = html`<div class="tour-card-meta">
        <span>${run.tour.title} · ${run.index + 1} of ${n}</span>
        <button class="tour-card-x" title="Stop the tour (Esc)" aria-label="Stop the tour" @click=${() => stopTour()}>✕</button>
      </div>
      <div class="tour-bar" aria-hidden="true">
        ${run.steps.map((_, i) => html`<i class=${i <= run.index ? 'done' : ''}></i>`)}
      </div>`;
    if (this.mode === 'missing') {
      return html`${meta}
        <h3>This control is not on the page</h3>
        <p>The page may have changed, or this step needs something you cannot see.</p>
        <div class="tour-card-acts">
          <button class="btn btn-ghost tour-card-back" @click=${() => stopTour()}>Stop the tour</button>
          <button class="btn btn-primary" @click=${() => moveStep(1)}>${last ? 'Finish' : 'Skip step'}</button>
        </div>`;
    }
    if (this.mode === 'failed') {
      return html`${meta}
        <h3>${step.title}</h3>
        <p>${this.failure}</p>
        <div class="tour-card-acts">
          <button class="btn btn-ghost tour-card-back" @click=${() => stopTour()}>Stop the tour</button>
          <button class="btn btn-primary" @click=${() => moveStep(-1)}>Back</button>
        </div>`;
    }
    if (this.mode === 'waiting') {
      return html`${meta}
        <h3>${step.title}</h3>
        <p>One moment…</p>`;
    }
    // The tour to offer at the end. Finishing this one is what unlocks it.
    const nextTour = run.tour.next ? tourById(run.tour.next) : undefined;
    const next =
      nextTour && nextTour.available() && (!lockedBy(nextTour) || lockedBy(nextTour)?.id === run.tour.id)
        ? nextTour
        : null;
    const hint = this.doneAtEntry
      ? 'Done already. Click Next to go on.'
      : step.advance === 'click'
        ? 'Click the lit control.'
        : typeof step.advance === 'object'
          ? 'Do it on the lit control to go on.'
          : '';
    return html`${meta}
      <h3>${step.title}</h3>
      <p>${step.text}</p>
      ${hint ? html`<p class="tour-card-hint">${hint}</p>` : nothing}
      ${last && next
        ? html`<button
            class="tour-card-next"
            @click=${() => {
              finishTour();
              startTour(next.id);
            }}
          ><span>Next tour: <strong>${next.title}</strong></span><i aria-hidden="true">→</i></button>`
        : nothing}
      <div class="tour-card-acts">
        ${run.index > 0
          ? html`<button class="btn btn-ghost tour-card-back" @click=${() => moveStep(-1)}>Back</button>`
          : html`<span class="tour-card-back"></span>`}
        ${step.skippable && !this.doneAtEntry && step.advance !== 'next'
          ? html`<button class="btn btn-ghost" @click=${() => moveStep(1)}>Skip</button>`
          : nothing}
        ${step.advance === 'next' || this.doneAtEntry
            ? html`<button class="btn btn-primary" @click=${() => moveStep(1)}>${last ? 'Finish' : 'Next'}</button>`
            : nothing}
      </div>`;
  }

  override render(): TemplateResult | typeof nothing {
    if (!runningTour.value) return nothing;
    return html`
      <div class="tour-dim t"></div>
      <div class="tour-dim b"></div>
      <div class="tour-dim l"></div>
      <div class="tour-dim r"></div>
      <div class="tour-ring" hidden></div>
      <div class="tour-card" role="dialog" aria-live="polite" aria-label="Guided tour">${this.renderCard()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-tour': AppTour;
  }
}
